import React, { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Redirect, Link } from 'react-router-dom';
import { InstantSearch } from 'react-instantsearch-dom';
import classNames from 'classnames';

import { REACT_APP_ALGOLIA_INDUSTRIES_INDEX } from 'constants/environment';
import { useFormatDate, useAlgoliaSearch } from 'hooks';
import { industriesCleanUp, deleteIndustry } from 'state/actions/industries';
import paths from 'pages/Router/paths';
import { Table, SearchBox } from 'components/Algolia';
import Modal from 'components/Modal';
import { roles, initAlgoliaClient } from 'utils';
import classes from './Industries.module.scss';

const searchClient = initAlgoliaClient();
const industriesIndex = REACT_APP_ALGOLIA_INDUSTRIES_INDEX;

const Industries = () => {
  const { role, loading, deleted } = useSelector(
    (state) => ({
      role: state.auth.userData.role,
      loading: state.industries.loading,
      deleted: state.industries.deleted,
    }),
    shallowEqual
  );

  const dispatch = useDispatch();

  const [deleteModal, setDeleteModal] = React.useState({
    industryId: null,
    isOpen: false,
  });

  const { search, refresh } = useAlgoliaSearch();

  useEffect(() => {
    return () => dispatch(industriesCleanUp());
  }, [dispatch]);

  useEffect(() => {
    if (deleted) {
      refresh();
      setDeleteModal((prevState) => ({
        industryId: null,
        isOpen: !prevState.isOpen,
      }));
      dispatch(industriesCleanUp());
    }
  }, [deleted, refresh, dispatch]);

  const onRemoveButtonClickHandler = useCallback((industryId) => {
    setDeleteModal((prevState) => ({
      industryId,
      isOpen: !prevState.isOpen,
    }));
  }, []);

  const onCloseModalHandler = useCallback(() => {
    setDeleteModal({ industryId: null, isOpen: false });
  }, []);

  const onDeleteIndustryHandler = () => {
    dispatch(deleteIndustry(deleteModal.industryId));
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'displayName',
        Cell: ({ row }) => (
          <Link to={`${paths.INDUSTRIES}/${row.original.objectID}`}>
            {row.original.displayName}
          </Link>
        ),
      },
      {
        Header: 'Created at',
        accessor: 'createdAt',
        Cell: ({ row }) => (
          <small className="has-text-grey is-abbr-like">
            {useFormatDate(row.original.createdAt, {
              weekday: 'short',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </small>
        ),
      },
      {
        Header: 'Created by',
        accessor: 'createdBy',
        Cell: ({ row }) => (
          <small className="has-text-grey is-abbr-like">
            {row.original.createdBy}
          </small>
        ),
      },
      {
        Header: '',
        accessor: 'actions',
        Cell: ({ row }) => (
          <>
            <div className={classNames('buttons is-right', classes.actions)}>
              <Link
                to={`${paths.INDUSTRIES}/${row.original.objectID}`}
                className="button is-small is-primary"
              >
                <span className="icon is-small">
                  <i className="mdi mdi-account-edit" />
                </span>
              </Link>

              <button
                type="button"
                className="button is-small is-danger"
                onClick={() =>
                  onRemoveButtonClickHandler(row.original.objectID)
                }
              >
                <span className="icon is-small">
                  <i className="mdi mdi-trash-can" />
                </span>
              </button>
            </div>
          </>
        ),
      },
    ],
    [onRemoveButtonClickHandler]
  );

  const redirect = useMemo(
    () => role !== roles.ADMIN && <Redirect to={paths.ROOT} />,
    [role]
  );

  return (
    <>
      {redirect}
      {deleteModal.isOpen && (
        <Modal
          isActive={deleteModal.isOpen}
          isLoading={loading}
          confirmButtonMessage="Delete"
          title="Confirm action"
          body="This will permanently delete the industry. Action can not be undone."
          cancelButtonMessage="Cancel"
          onConfirmation={onDeleteIndustryHandler}
          onCancel={onCloseModalHandler}
        />
      )}
      <InstantSearch
        searchClient={searchClient}
        indexName={industriesIndex}
        refresh={search.refresh}
      >
        <section className="hero is-hero-bar">
          <div className="hero-body">
            <div className="level">
              <div className="level-left">
                <div className="level-item">
                  <h1 className="title">Industries</h1>
                </div>
              </div>
              <div className="level-right">
                <div className="level-item">
                  <Link to={paths.ADD_INDUSTRY} className="button">
                    New industry
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="section is-main-section">
          <div className="card has-table has-mobile-sort-spaced">
            <header className="card-header">
              <p className="card-header-title table-header">
                <span>Search</span>
                <SearchBox />
              </p>
            </header>
            <Table columns={columns} defaultPerPageOption={10} />
          </div>
        </section>
      </InstantSearch>
    </>
  );
};

export default Industries;
